import { useCallback } from "react";
import { useHttpAdapter } from "./useHttpAdapter";
import {
  CreatePostResponse,
  MediaUploadResponse,
  PostContentPayload,
  SubredditResponse,
} from "../types/post-content";

const usePfmPostSocialApi = () => {
  const { post, get, put, del } = useHttpAdapter();

  const createPost = useCallback(
    async (payload: PostContentPayload) => {
      try {
        const response = await post<CreatePostResponse>(
          "/pfmSocialPost/post/multiplatform",
          payload
        );
        return response.data;
      } catch {
        return undefined;
      }
    },
    [post]
  );

  const uploadMedia = useCallback(
    async (media: File[]) => {
      const formData = new FormData();
      media.forEach((file) => {
        formData.append("file", file);
      });
      const response = await post<MediaUploadResponse>("/media", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.status === 201) {
        const content_type = response.data.content_type;
        if (content_type.includes("image")) {
          return { type: "image", url: response.data.path };
        } else if (content_type.includes("video")) {
          return { type: "video", url: response.data.path };
        }
      }
      return undefined;
    },
    [post]
  );

  const searchSubreddit = useCallback(
    async (query: string): Promise<string[]> => {
      try {
        const response = await get<SubredditResponse>(
          `/pfmSocialPost/sub-reddit/search?query=${query}`
        );
        if (response.status === 200) {
          return response.data.data;
        }
        return [];
      } catch {
        return [];
      }
    },
    [get]
  );

  const updatePost = useCallback(
    async (payload: PostContentPayload, postId: string, logId: string) => {
      try {
        const response = await put<CreatePostResponse>(
          `/pfmSocialPost/update/post/${postId}?log_id=${logId}`,
          payload
        );
        return response.data;
      } catch {
        return undefined;
      }
    },
    [put]
  );

  const deletePost = useCallback(
    async (postId: string): Promise<boolean> => {
      try {
        await del(`pfmSocialPost/delete/${postId}`);
        return true;
      } catch {
        return false;
      }
    },
    [del]
  );

  return { createPost, uploadMedia, searchSubreddit, updatePost, deletePost };
};

export default usePfmPostSocialApi;
